import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { formatQueryParams } from '@utils/Format'
import { downloadBase64 } from '@utils/Download'
import { feeFixo as endpoint } from '@modules/fee-fixo/constants/URL'

// A geração passa por duas Lambdas (HTML e depois PDF), então o resultado não
// vem na resposta do POST — o front acompanha por polling.
const INTERVALO_POLLING_MS = 3000
const MAX_TENTATIVAS = 40
const STEP_CONCLUIDO = -1

/**
 * Teto de espera pelo PDF, em RELÓGIO (spec-022, [G-4] do QA da 021).
 *
 * `MAX_TENTATIVAS` sozinho não limita o tempo: ele conta iterações, e cada uma
 * custa o intervalo MAIS a latência da consulta. Com o polling respondendo em
 * ~2 s, as 40 tentativas viram ~3min20 — o QA mediu 4 minutos de botão
 * desabilitado sem nenhuma mensagem, e a promessa que a tela faz ao consultor é
 * de tempo, não de contagem. Os dois tetos coexistem: vale o que vier primeiro.
 */
const TETO_ESPERA_MS = 120000

// Uma frase só para os dois caminhos de espera (baixar e enviar) — a mensagem
// diz o que aconteceu E o que fazer a seguir.
const MSG_ESPERA_ESGOTADA =
  'A geração está demorando mais que o esperado. Tente novamente em alguns minutos; se persistir, contate o suporte.'

// Validação de formato só para evitar disparar uma geração inteira por causa de
// um erro de digitação. A validação que vale é a do backend.
const FORMATO_EMAIL = /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]{2,}$/

const ETAPA_GERANDO = 'gerando'
const ETAPA_ENVIANDO = 'enviando'

// O envelope de download do backend não traz nome nem extensão do arquivo.
const PREFIXO_ARQUIVO = 'relatorio-fee-fixo'
const EXTENSAO_RELATORIO = 'pdf'

/**
 * Relatório PDF de migração para fee-based.
 *
 * Stateless: nada vai para a store. O documento é gerado a partir de uma
 * análise já salva — o relatório não recalcula nada.
 */
class ApiFeeFixoRelatorio extends APITemplate {
  /**
   * Dispara a geração.
   *
   * @param {string} opts.analiseId
   * @param {string[]|null} [opts.secoes] As seções escolhidas. `null`/ausente =
   *   documento inteiro, que é o comportamento de sempre. O campo só entra no
   *   corpo quando restringe: mandar a lista completa produziria o mesmo PDF,
   *   mas faria o backend distinguir dois casos que são o mesmo.
   */
  async gerar({ analiseId, secoes = null } = {}) {
    if (!analiseId) {
      return this.buildErrorResponse({
        message: 'Guarde o cenário antes de gerar o relatório.',
        value: null,
        status: 400,
      })
    }
    const corpo = { analise_id: analiseId }
    if (secoes && secoes.length) corpo.secoes = secoes
    const r = await con.post(endpoint.relatorios, corpo)
    return (
      r ||
      this.buildErrorResponse({
        message:
          'Não foi possível iniciar a geração do relatório. Tente novamente mais tarde.',
        value: null,
      })
    )
  }

  /**
   * Progresso da geração.
   *
   * `analiseId` não é opcional na prática: é dele que o backend deriva o caminho
   * do arquivo no S3 — o mesmo que a geração usou. Sem ele, o backend cai no
   * `idClienteApp` recebido, que vem de `app_clientes` e aponta para outro
   * prefixo: o polling nunca vê "concluído" e o card espera até o teto de 2 min
   * com o PDF pronto do outro lado (defeito medido em 30/07).
   */
  async consultarStatus({ hash, idClienteApp, analiseId } = {}) {
    const url =
      endpoint.relatorioStatus(hash) +
      formatQueryParams({ id_cliente_app: idClienteApp, analise_id: analiseId })
    const r = await con.get(url)
    return (
      r ||
      this.buildErrorResponse({
        message: 'Não foi possível consultar o status do relatório.',
        value: null,
      })
    )
  }

  /**
   * Baixa o PDF pronto.
   *
   * O backend devolve o arquivo em `value.pdf` (`api_pdf.py::download_pdf`) —
   * não em `value.base64`, e sem `name`/`extension`. Ler a chave errada não dá
   * erro: a resposta chega com status 200 e "PDF encontrado", o snack diz que
   * deu certo e o arquivo nunca baixa (defeito medido no QA da spec-008 em
   * 30/07). Por isso o nome do arquivo é montado aqui, no cliente.
   */
  async baixar({ hash, idClienteApp, analiseId, codigoCliente } = {}) {
    const url =
      endpoint.relatorioStatus(hash) +
      formatQueryParams({
        id_cliente_app: idClienteApp,
        analise_id: analiseId,
        download: true,
      })
    const r = await con.get(url)
    const erroPadrao = this.buildErrorResponse({
      message:
        'Não foi possível baixar o relatório. Gere novamente se o problema persistir.',
      value: null,
    })
    if (!r) return erroPadrao
    if (r.status !== 200) return r

    // `base64` é tolerância caso o backend padronize o envelope de download.
    const b64 = r.value && (r.value.pdf || r.value.base64)
    if (!b64) return erroPadrao

    downloadBase64(b64, this._nomeArquivo(codigoCliente), EXTENSAO_RELATORIO)
    return r
  }

  _nomeArquivo(codigoCliente) {
    if (!codigoCliente) return PREFIXO_ARQUIVO
    return PREFIXO_ARQUIVO + '-' + codigoCliente
  }

  /**
   * Envia por e-mail um relatório JÁ gerado, com o PDF anexo.
   *
   * Ação irreversível: só deve ser chamada depois de o consultor confirmar no
   * card. Quem prepara o texto é o agente; quem dispara é o clique.
   *
   * @param {Object} opts
   * @param {string} opts.hash          Identificador do relatório gerado.
   * @param {number} opts.idClienteApp  Cliente dono do relatório.
   * @param {string} opts.analiseId     Cenário que originou o PDF.
   * @param {string} opts.destinatario  Endereço confirmado pelo consultor.
   */
  async enviarPorEmail({ hash, idClienteApp, analiseId, destinatario } = {}) {
    const email = (destinatario || '').trim()
    if (!email || !FORMATO_EMAIL.test(email)) {
      return this.buildErrorResponse({
        message: 'Informe um e-mail válido para enviar o relatório.',
        value: null,
        status: 400,
      })
    }
    const r = await con.post(endpoint.relatorioEnviarEmail(hash), {
      id_cliente_app: idClienteApp,
      analise_id: analiseId,
      destinatario: email,
    })
    return (
      r ||
      this.buildErrorResponse({
        message:
          'Não foi possível enviar o relatório. Tente novamente em instantes.',
        value: null,
      })
    )
  }

  /**
   * Gera o relatório, aguarda ficar pronto e envia por e-mail.
   *
   * Irmão de `gerarEBaixar`: mesmo polling, desfecho diferente. A espera de até
   * ~2 minutos vive aqui, no cliente, e não numa requisição HTTP aberta — foi a
   * decisão que permitiu manter o agente fora do caminho do envio.
   *
   * INVARIANTE: `enviarPorEmail` só é chamado quando o PDF está pronto
   * (`step === STEP_CONCLUIDO`). Mandar um e-mail sem o anexo é o pior desfecho
   * possível deste fluxo.
   */
  /**
   * Aguarda o PDF ficar pronto, informando a etapa ao chamador.
   *
   * @returns {Object} `{ pronto: true }` quando concluiu, ou o envelope de erro
   *   do passo que falhou — que fala da GERAÇÃO, nunca do e-mail.
   */
  async _aguardarPdfPronto({
    hash,
    idClienteApp,
    analiseId,
    onProgresso,
    intervaloMs,
    maxTentativas,
    tetoMs = TETO_ESPERA_MS,
    agora = Date.now,
  }) {
    // Dois limites na MESMA condição: as tentativas e o relógio. Vale o que
    // vier primeiro — ver `TETO_ESPERA_MS`.
    const prazo = agora() + tetoMs
    for (
      let tentativa = 0;
      tentativa < maxTentativas && agora() < prazo;
      tentativa += 1
    ) {
      await new Promise((resolve) => setTimeout(resolve, intervaloMs))
      const status = await this.consultarStatus({
        hash,
        idClienteApp,
        analiseId,
      })

      if (status && status.status !== 200) return status
      if (status && status.step === STEP_CONCLUIDO) return { pronto: true }
      if (typeof onProgresso === 'function') onProgresso(ETAPA_GERANDO)
    }

    return this.buildErrorResponse({
      message: MSG_ESPERA_ESGOTADA,
      value: null,
    })
  }

  /**
   * Gera o relatório, aguarda ficar pronto e envia por e-mail.
   *
   * Irmão de `gerarEBaixar`: mesmo polling, desfecho diferente. A espera de até
   * ~2 minutos vive aqui, no cliente, e não numa requisição HTTP aberta — foi a
   * decisão que permitiu manter o agente fora do caminho do envio.
   *
   * INVARIANTE: `enviarPorEmail` só é chamado quando o PDF está pronto. Mandar
   * um e-mail sem o anexo é o pior desfecho possível deste fluxo.
   */
  async gerarEEnviar({
    analiseId,
    idClienteApp,
    destinatario,
    // ★ 12/08/2026 — as seções escolhidas viajam junto até o disparo. `null` é
    // o default e significa documento inteiro, que é o que todo chamador
    // anterior a esta feature continua passando (por omissão).
    secoes = null,
    onProgresso,
    intervaloMs = INTERVALO_POLLING_MS,
    maxTentativas = MAX_TENTATIVAS,
    tetoMs = TETO_ESPERA_MS,
    // Injetável pelo mesmo motivo de `intervaloMs`: o teste do teto de relógio
    // não pode depender do relógio real nem de fake timers.
    agora = Date.now,
  } = {}) {
    const email = (destinatario || '').trim()
    if (!email || !FORMATO_EMAIL.test(email)) {
      return this.buildErrorResponse({
        message: 'Informe um e-mail válido para enviar o relatório.',
        value: null,
        status: 400,
      })
    }

    const disparo = await this.gerar({ analiseId, secoes })
    if (!disparo || disparo.status !== 200) return disparo

    const hash = disparo.value && disparo.value.hash
    if (!hash) {
      return this.buildErrorResponse({
        message:
          'O relatório foi solicitado, mas não recebemos o identificador.',
        value: null,
      })
    }

    if (typeof onProgresso === 'function') onProgresso(ETAPA_GERANDO)

    const espera = await this._aguardarPdfPronto({
      hash,
      idClienteApp,
      analiseId,
      onProgresso,
      intervaloMs,
      maxTentativas,
      tetoMs,
      agora,
    })
    if (!espera.pronto) return espera

    if (typeof onProgresso === 'function') onProgresso(ETAPA_ENVIANDO)
    return this.enviarPorEmail({
      hash,
      idClienteApp,
      analiseId,
      destinatario: email,
    })
  }

  /**
   * Dispara, acompanha e baixa. Devolve o envelope do passo que falhou, se
   * algum falhar — o chamador só precisa dar o feedback.
   */
  async gerarEBaixar({
    analiseId,
    idClienteApp,
    codigoCliente,
    // ★ 12/08/2026 — as seções escolhidas viajam junto até o disparo. `null` é
    // o default e significa documento inteiro, que é o que todo chamador
    // anterior a esta feature continua passando (por omissão).
    secoes = null,
    onProgresso,
    // Injetável para o teste não depender de fake timers (o Jest desta versão
    // não tem runAllTimersAsync).
    intervaloMs = INTERVALO_POLLING_MS,
    maxTentativas = MAX_TENTATIVAS,
    tetoMs = TETO_ESPERA_MS,
    agora = Date.now,
  } = {}) {
    const disparo = await this.gerar({ analiseId, secoes })
    if (!disparo || disparo.status !== 200) return disparo

    const hash = disparo.value && disparo.value.hash
    if (!hash) {
      return this.buildErrorResponse({
        message:
          'O relatório foi solicitado, mas não recebemos o identificador.',
        value: null,
      })
    }

    // Mesma espera de `_aguardarPdfPronto`: tentativas E relógio na condição.
    const prazo = agora() + tetoMs
    for (
      let tentativa = 0;
      tentativa < maxTentativas && agora() < prazo;
      tentativa += 1
    ) {
      await new Promise((resolve) => setTimeout(resolve, intervaloMs))
      const status = await this.consultarStatus({
        hash,
        idClienteApp,
        analiseId,
      })

      if (status && status.status !== 200) return status
      if (typeof onProgresso === 'function') onProgresso(status)
      if (status && status.step === STEP_CONCLUIDO) {
        return this.baixar({ hash, idClienteApp, analiseId, codigoCliente })
      }
    }

    return this.buildErrorResponse({
      message: MSG_ESPERA_ESGOTADA,
      value: null,
    })
  }

  /**
   * Espera o relatório ficar pronto e baixa — SEM disparar a geração.
   *
   * ★ spec-006 (unificar-relatorio-e-apresentacao, [T008]) — o modelo
   * Relatório passou a poder nascer pela porta da apresentação
   * (`apiFeeFixoApresentacao.gerar({modelo: 'relatorio', ...})`), que já
   * dispara a Lambda e devolve `{hash, id_cliente_app}` na resposta do POST
   * (o backend delega para o MESMO `UseCaseFeeFixoRelatorio.gerar()` que
   * este `gerar()` chama). Chamar `this.gerar()` de novo aqui disparava uma
   * SEGUNDA geração pelo mesmo cenário — puro desperdício, e o backend não
   * tem idempotência por hash nesse caminho (diferente da apresentação).
   *
   * Mesmo polling de `gerarEBaixar` (mesma condição de tentativas × relógio,
   * mesmo teto), só que começando DEPOIS do disparo — é `gerarEBaixar` menos
   * o `this.gerar()` inicial.
   */
  async aguardarEBaixar({
    hash,
    idClienteApp,
    analiseId,
    codigoCliente,
    onProgresso,
    intervaloMs = INTERVALO_POLLING_MS,
    maxTentativas = MAX_TENTATIVAS,
    tetoMs = TETO_ESPERA_MS,
    agora = Date.now,
  } = {}) {
    if (!hash) {
      return this.buildErrorResponse({
        message:
          'O relatório foi solicitado, mas não recebemos o identificador.',
        value: null,
      })
    }

    const prazo = agora() + tetoMs
    for (
      let tentativa = 0;
      tentativa < maxTentativas && agora() < prazo;
      tentativa += 1
    ) {
      await new Promise((resolve) => setTimeout(resolve, intervaloMs))
      const status = await this.consultarStatus({
        hash,
        idClienteApp,
        analiseId,
      })

      if (status && status.status !== 200) return status
      if (typeof onProgresso === 'function') onProgresso(status)
      if (status && status.step === STEP_CONCLUIDO) {
        return this.baixar({ hash, idClienteApp, analiseId, codigoCliente })
      }
    }

    return this.buildErrorResponse({
      message: MSG_ESPERA_ESGOTADA,
      value: null,
    })
  }
}

const apiFeeFixoRelatorio = new ApiFeeFixoRelatorio()
export { apiFeeFixoRelatorio }
export default apiFeeFixoRelatorio
